import { Colors, Lightning } from '@lightningjs/sdk';
import { LiveEvent, ShowDetails } from 'types/api/media';
import { STANDARD_FADE } from 'support/animations';
import MetadataLabel from 'components/common/MetadataLabel';
import { getImageTextureContainObj, ttsTagRemoval } from 'support/generalUtils';
import { getFontFaceFromStyle } from 'support/textUtils';
import { constants } from 'aliases';
import TextLabel from '../TextLabel';

export interface MoreInfoModalTemplateSpec
  extends Lightning.Component.TemplateSpec {
  mediaContent: ShowDetails | LiveEvent | undefined;

  Background: Lightning.Component;
  Container: {
    ImageLogo: Lightning.Component;
    Title: typeof TextLabel;
    Metadata: typeof MetadataLabel;
    Description: typeof TextLabel;
  };
}

const BACKGROUND_WIDTH = 1920;
const BACKGROUND_HEIGHT = 1080;

const CONTAINER_MOUNT_X = 0.5;
const CONTAINER_MOUNT_POINT_X = 960;
const CONTAINER_MOUNT_POINT_Y = 175;

const LOGO_WIDTH = 565;
const LOGO_HEIGHT = 230;

export const FONT_SIZE = 29;

const TITLE_MAX_LINES = 1;
const TITLE_MARGIN_TOP = 53;

export const DESCRIPTION_WIDTH = 1176;
const DESCRIPTION_MAX_LINES = 8;
export const DESCRIPTION_LINE_HEIGHT = 34;
const DESCRIPTION_MARGIN_TOP = 36;

export default abstract class MoreInfoModal<
    TemplateSpec extends MoreInfoModalTemplateSpec = MoreInfoModalTemplateSpec,
  >
  extends Lightning.Component<TemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<MoreInfoModalTemplateSpec>
{
  protected _Container = (this as MoreInfoModal).getByRef('Container')!;
  protected _ImageLogo = this._Container.getByRef('ImageLogo')!;
  protected _Title = this._Container.getByRef('Title')!;
  protected _Metadata = this._Container.getByRef('Metadata')!;
  protected _Description = this._Container.getByRef('Description')!;

  protected _mediaContent: TemplateSpec['mediaContent'] = undefined;

  get title() {
    return [
      ttsTagRemoval(this._Title.text?.text ?? ''),
      ttsTagRemoval(this._Metadata.title ?? ''),
      ttsTagRemoval(this._Description.text?.text ?? ''),
    ];
  }

  set mediaContent(mediaContent: ShowDetails | LiveEvent) {
    this._mediaContent = mediaContent;
    this.updateTitle(mediaContent);
    this.updateDescription(mediaContent);
    this.updateMetadata(mediaContent);
    this.updateLogo();
  }

  protected abstract get logoSrc(): string;

  static override _template() {
    return {
      transitions: STANDARD_FADE,
      Background: {
        zIndex: constants.ui.zIndex.modalLayer,
        collision: true,
        rect: true,
        h: BACKGROUND_HEIGHT,
        w: BACKGROUND_WIDTH,
        color: Colors('modalBackground').get(),
      },
      Container: {
        zIndex: constants.ui.zIndex.modalLayer,
        mountX: CONTAINER_MOUNT_X,
        x: CONTAINER_MOUNT_POINT_X,
        y: CONTAINER_MOUNT_POINT_Y,
        flex: {
          direction: 'column',
          alignItems: 'center',
        },
        ImageLogo: {
          h: LOGO_HEIGHT,
          w: LOGO_WIDTH,
        },
        Title: {
          type: TextLabel,
          flexItem: { marginTop: TITLE_MARGIN_TOP },
          visible: false,
          w: DESCRIPTION_WIDTH,
          text: {
            textAlign: 'center',
            fontSize: FONT_SIZE,
            fontFace: getFontFaceFromStyle('bold'),
            maxLines: TITLE_MAX_LINES,
          },
        },
        Metadata: {
          type: MetadataLabel,
          w: DESCRIPTION_WIDTH,
          shouldCenter: true,
        },
        Description: {
          type: TextLabel,
          flexItem: { marginTop: DESCRIPTION_MARGIN_TOP },
          w: DESCRIPTION_WIDTH,
          text: {
            advancedRenderer: true,
            maxLines: DESCRIPTION_MAX_LINES,
            lineHeight: DESCRIPTION_LINE_HEIGHT,
            fontSize: FONT_SIZE,
          },
        },
      },
    };
  }

  override _active() {
    this._ImageLogo.on('txError', () => {
      this._ImageLogo.patch({ texture: undefined });
    });
  }

  override _inactive() {
    this._ImageLogo.removeAllListeners('txError');
  }

  protected abstract updateTitle(mediaContent: ShowDetails | LiveEvent): void;

  protected updateMetadata(mediaContent: ShowDetails | LiveEvent) {
    this._Metadata.patch({ mediaContent });
  }

  protected updateDescription(mediaContent: ShowDetails | LiveEvent) {
    this._Description.patch({ text: mediaContent.description });
  }

  protected updateLogo() {
    const logoTexture = getImageTextureContainObj(
      this.logoSrc,
      LOGO_WIDTH,
      LOGO_HEIGHT,
    );
    this._ImageLogo.patch(logoTexture);
  }
}
